import type { ColumnSizingState } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTableSettings, useTableStoreState } from '../../hooks';
import { cn } from '../../styles/lib/utils';
import type { GridSpacing } from '../Grid';
import { GridCellText } from '../GridCellText';
import { Table, type TableColumn } from '../Table';
import { CategoryPill } from './CategoryPill';
import { LayerLibraryToolbar } from './LayerLibraryToolbar';
import { defaultLayerInfo } from './layerInfo';
import { LayerAddCell, LayerNameInfoCell, LayerTableCellContext } from './layerTableCells';
import { searchLayers } from './searchLayers';
import type { LayerInfo, LayerLibraryRow } from './types';

/** Format an Updated timestamp for display (e.g. 7/31/2024). */
function formatUpdated(date: Date): string {
  return date.toLocaleDateString('en-US');
}

const COLUMNS: TableColumn<LayerLibraryRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: 300,
    enableResizing: false,
    cell: ({ row }) => <LayerNameInfoCell row={row.original} />,
  },
  {
    id: 'category',
    accessorKey: 'category',
    header: 'Category',
    size: 200,
    // The fill column (expandColumnId below): drag-resizing it is meaningless while
    // it absorbs the leftover width and would fight the flex growth.
    enableResizing: false,
    cell: ({ row }) => <CategoryPill category={row.original.category} />,
  },
  // Last so it renders at the right edge whether inline (roomy) or pinned (tight) —
  // crossing the pin threshold must not move the column, only add the pinned strip.
  {
    id: 'action',
    header: '',
    size: 85,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }) => <LayerAddCell row={row.original} />,
  },
  // Commented out for now until we have data for these fields
  // {
  //   id: 'source',
  //   accessorKey: 'source',
  //   header: 'Source',
  //   size: 126,
  //   cell: ({ row }) => <GridCellText>{row.original.source ?? ''}</GridCellText>,
  // },
  // {
  //   id: 'updated',
  //   accessorFn: (row) => row.updated,
  //   header: 'Updated',
  //   size: 100,
  //   sortingFn: 'datetime',
  //   cell: ({ row }) => (
  //     <GridCellText>{row.original.updated ? formatUpdated(row.original.updated) : ''}</GridCellText>
  //   ),
  // },
  // {
  //   id: 'description',
  //   accessorKey: 'description',
  //   header: 'Description',
  //   size: 280,
  //   cell: ({ row }) => <GridCellText>{row.original.description ?? ''}</GridCellText>,
  // },
];

export type LayerLibraryTableProps = {
  /** Stable id — used for the table root test id and the localStorage key for column sizes. */
  id?: string;
  /** All catalog rows. The server returns everything; the client paginates/searches/sorts. */
  rows: LayerLibraryRow[];
  /** Called when a row's Add action is clicked. */
  onAddLayer?: (row: LayerLibraryRow) => void;
  /**
   * Called once per committed, non-empty search (debounced) with the trimmed query.
   * For analytics — the consumer decides what (if anything) to record; this component
   * stays free of analytics dependencies.
   */
  onSearch?: (query: string) => void;
  /**
   * Resolves the detail shown in the info modal for a row. Defaults to dummy data built
   * from the row; the consumer supplies real layer detail here.
   */
  getLayerInfo?: (row: LayerLibraryRow) => LayerInfo;
  isLoading?: boolean;
  /** Rendered when there are no rows after search filtering. */
  emptyState?: React.ReactNode;
  /** Default page size (rows per page). */
  defaultPageSize?: number;
  /** Row density. Defaults to `comfy`. */
  spacing?: GridSpacing;
  className?: string;
  /** Names the table for screen readers; prefer `aria-labelledby` when a visible heading sits above it. */
  'aria-label'?: string;
  /** Id of the visible heading that names the table for screen readers. */
  'aria-labelledby'?: string;
};

/**
 * Presentational Layer Library catalog table.
 *
 * Client-side: state lives in `useTableStoreState` (not the URL); the server returns all
 * rows and the client handles search, sort, and pagination. Sortable + searchable +
 * paginated + resizable columns (sizes persisted to localStorage by `id`); not filterable,
 * pinnable, or selectable.
 */
function LayerLibraryTable({
  id = 'layer-library-table',
  rows,
  onAddLayer,
  onSearch,
  getLayerInfo = defaultLayerInfo,
  isLoading = false,
  emptyState,
  defaultPageSize = 50,
  spacing = 'comfy',
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: LayerLibraryTableProps) {
  const stateControl = useTableStoreState({ defaultPageSize });
  const { globalFilter } = stateControl;

  // Report a committed, non-empty search to the consumer once typing settles. Debounced
  // so a burst of keystrokes yields one analytics event, not one per character.
  useEffect(() => {
    const query = globalFilter?.trim();
    if (!onSearch || !query) return;
    const timer = setTimeout(() => onSearch(query), 500);
    return () => clearTimeout(timer);
  }, [globalFilter, onSearch]);

  // Row whose info popover is open — drives the active button + row highlight.
  const [openInfoRowId, setOpenInfoRowId] = useState<string | null>(null);
  const highlightedRowIds = useMemo(() => (openInfoRowId ? new Set([openInfoRowId]) : undefined), [openInfoRowId]);
  const cellContext = useMemo(
    () => ({ openInfoRowId, setOpenInfoRowId, getLayerInfo, onRowAction: onAddLayer }),
    [openInfoRowId, getLayerInfo, onAddLayer],
  );

  // Persist column widths to localStorage (write-only: read once on mount, debounced save).
  const { initialColumnSizing, saveSettings } = useTableSettings({ id });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(initialColumnSizing ?? {});
  useEffect(() => {
    saveSettings({ columnSizing });
  }, [columnSizing, saveSettings]);

  // Search in the wrapper so the toolbar gets an accurate total without double-filtering.
  // The base Table then owns client-side sorting + pagination of the filtered rows.
  const filteredRows = useMemo(() => searchLayers(rows, globalFilter), [rows, globalFilter]);

  // Pin the Add action right only when the container can't fit every column at its
  // configured width — inline when roomy (no pinned strip), pinned when the center
  // would otherwise scroll the action out of view. The container's width comes from
  // the page flex layout (not from the table's content), so toggling the pin can't
  // re-trigger the measurement.
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const [isActionPinned, setIsActionPinned] = useState(false);
  const totalColumnsWidth = useMemo(
    // Configured (or user-persisted) column sizes plus the Table root's 1px borders.
    () => COLUMNS.reduce((sum, col) => sum + (columnSizing[col.id] ?? col.size ?? 0), 0) + 2,
    [columnSizing],
  );
  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const update = () => setIsActionPinned(el.clientWidth < totalColumnsWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [totalColumnsWidth]);
  const columnPinning = useMemo(() => ({ left: [], right: isActionPinned ? ['action'] : [] }), [isActionPinned]);

  const showEmptyState = emptyState && !isLoading && filteredRows.length === 0;

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid={id}>
      <LayerLibraryToolbar stateControl={stateControl} totalItems={filteredRows.length} isLoading={isLoading} />
      <div ref={tableWrapRef} className="relative flex min-h-0 flex-1 flex-col">
        {showEmptyState ? (
          <div className="border-grey-20 bg-grey-5 flex flex-1 flex-col items-center justify-center gap-4 self-stretch rounded border p-8">
            {emptyState}
          </div>
        ) : (
          <LayerTableCellContext.Provider value={cellContext}>
            <Table
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              data={filteredRows}
              columns={COLUMNS}
              stateControl={stateControl}
              // Rows are already filtered by searchLayers above; without this, TanStack re-filters
              // over the accessor columns only (name/category), diverging from the toolbar count.
              manualFiltering
              getRowId={(row) => row.id}
              highlightedRowIds={highlightedRowIds}
              spacing={spacing}
              enableSorting
              enableColumnResizing
              columnSizing={columnSizing}
              onColumnSizingChange={setColumnSizing}
              enableRowSelection={false}
              columnPinning={columnPinning}
              fixedPinning
              enableColumnPinning={false}
              enableColumnFilters={false}
              enableColumnOrdering={false}
              enableHiding={false}
              isLoading={isLoading}
              // Last column absorbs any leftover width so the header border and row
              // striping always reach the container edge
              expandColumnId="category"
            />
          </LayerTableCellContext.Provider>
        )}
      </div>
    </div>
  );
}

export { LayerLibraryTable };
